import { useState, useEffect, useRef, ReactNode } from "react";
import Search from "../../Icons/Search";

export type OptionType = {
  label: string;
  value: string;
  bold?: string; // optional part to bold
};

type CustomSelectProps = {
  options: OptionType[];
  placeholder?: string;
  enableFilter?: boolean;
  onSelect?: (option: OptionType) => void;
  icon?: ReactNode;
  defaultValue?: OptionType;
  onChange?: (option: OptionType) => void;
};

const renderLabel = (label: string, bold?: string) => {
  // if (!bold || !label.includes(bold)) return label;
  if (!bold) return label;

  const parts = label.split(bold);
  return (
    <>
      {parts[0]}
      <span className="fw-700">&nbsp;{bold}</span>
      {parts[1]}
    </>
  );
};

const SortBySelect = ({
  options,
  placeholder = "Select an option",
  onSelect,
  enableFilter = true,
  icon,
  defaultValue,
  onChange,
}: CustomSelectProps) => {
  const [selected, setSelected] = useState<OptionType | null>(
    defaultValue || null
  );
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !(dropdownRef.current as HTMLElement).contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (defaultValue) {
      setSelected(defaultValue);
    }
  }, [defaultValue]);

  // Filter options only if enableFilter is true
  const filteredOptions = enableFilter
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(search.toLowerCase())
      )
    : options;

  const handleSelect = (option: OptionType) => {
    setSelected(option);
    setIsOpen(false);
    setSearch("");
    if (onSelect) {
      onSelect(option);
      if (onChange) {
        onChange(option);
      }
    }
  };

  return (
    <div className="sortby-select position-lg-relative" ref={dropdownRef}>
      {/* Selected Item */}
      <div
        className="d-flex gap-3 align-items-center cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="d-flex gap-8 align-items-center">
          {icon && <>{icon}</>}
          <div
            className={`fs-13 fw-700 ${
              selected ? "text-black" : "text-grey-medium"
            }`}
          >
            {selected
              ? renderLabel(selected.label)
              : placeholder}
          </div>
        </div>
        <svg
          className={`min-w-12px ${isOpen ? "rotate-180" : "rotate-0"}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.97084 4.58654C10.263 4.29436 10.263 3.82064 9.97084 3.52846C9.67889 3.23651 9.20561 3.23625 8.91334 3.52788L5.99988 6.435L3.08642 3.52788C2.79414 3.23625 2.32087 3.23651 2.02892 3.52846C1.73674 3.82064 1.73674 4.29436 2.02892 4.58654L4.58567 7.14329C5.36671 7.92434 6.63304 7.92433 7.41409 7.14329L9.97084 4.58654Z"
            fill="black"
          />
        </svg>
      </div>
      {/* Dropdown List */}
      {isOpen && (
        <div className="min-w-200px dropdown-shadow position-absolute end-0 bg-teal-pale z-1 border border-black radius-8px overflow-hidden">
          {enableFilter && (
            <div className="p-6px border-bottom border-grey-light">
              <div className="form-group input-icon icon-left">
                <Search />
                <input
                  type="text"
                  className="form-control border-grey-light"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}
          <ul className="list-unstyled m-0 max-h-300px overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt: OptionType, index: number) => (
                <li
                  className={`cursor-pointer px-15px py-6px fs-16 fs-mobile-15 fw-400 text-black lh-20px d-block text-nowrap ${
                    index > 0 ? "border-top border-black" : ""
                  }`}
                  key={opt.value}
                  onClick={() => handleSelect(opt)}
                >
                  {renderLabel(opt.label, opt.bold)}
                </li>
              ))
            ) : (
              <li className="no-options px-15px py-6px fs-16 fw-400 text-grey-medium lh-20px d-block text-nowrap">
                No results found
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SortBySelect;
